import React, { useEffect, useState, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import RecordingNavigationBlocker from '../NavigationBlocker/RecordingNavigationBlocker';
import NavigationBlocker from '../NavigationBlocker/NavigationBlocker';
import posterImg from '../../video-camera.svg';
import DurationDisplay from './DurationDisplay';
import RecordingButton from './RecordingButton';
import MurphyStatusDisplay from './MurphyStatusDisplay';
import QAProgressIndicator from './QAProgressIndicator';
import useQASession from './useQASession';
import { QA_STATES } from './qaStateMachine';
import { computePlanState } from '../../pagesv2/TrainingPlan/planState';
import InvestorPitchChecklist from './InvestorPitchChecklist';
import { useHistory, Redirect } from 'react-router-dom';
import { ConferenceContainer } from './NewRecordPresentation.styles';
import { TileGrid, BottomBar, CountdownOverlay } from './CallTiles.styles';
import UserTile from './UserTile';
import ParticipantTile from './ParticipantTile';
import { participantGlow, tileCaption } from './tileGlow';

// Modes that capture the SCREEN rather than the camera. The user's tile shows
// what they are sharing, so "camera off" has no meaning in them and nothing may
// swap the share out for an avatar.
export const SCREEN_SHARE_TYPES = ['sales_presentation', 'sales_presentation_2', 'investor_pitch'];

// Maps a getUserMedia/getDisplayMedia DOMException to a recording-error variant for
// MurphyStatusDisplay, or null when it isn't a device/permission problem we surface.
const classifyMediaError = (err) => {
    if (!err) return null;
    if (err.name === 'NotAllowedError') return 'permission_denied';   // exists but blocked (browser/OS)
    if (err.name === 'NotFoundError' || err.name === 'NotReadableError' || err.name === 'OverconstrainedError') {
        return 'device_not_found';                                    // absent, disabled at OS level, or in use
    }
    return null;
};

const NewRecordPresentation = ({ videoPresentationStore, usersStore, assessmentsStore, companyStore, meetingStore }) => {
    const history = useHistory();
    const [recording, setRecording] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    // The confirm dialog's upload is running. Disables the dialog's buttons and
    // arms the leave-the-page prompt for the whole upload, not just the recording.
    const [uploading, setUploading] = useState(false);
    const [numberOfTries, setNumberOfTries] = useState(1);
    const [chunksBuffer, setChunksBuffer] = useState([]);
    const chunksBufferRef = useRef([]);
    const [mediaStream, setMediaStream] = useState(null);
    const mediaStreamRef = useRef(null);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const mediaRecorderRef = useRef(null);
    const [videoStatus, setVideoStatus] = useState('');
    const blobCountRef = useRef(0);
    const [currentInteraction, setCurrentInteraction] = useState(null);
    // Ref mirrors for the interaction timers: they fire from setTimeout
    // closures created at recording start, where state values are frozen at
    // their first render. Same rule as the capture teardown: timers read REFS.
    const currentInteractionRef = useRef(null);
    const isPlayingAudioRef = useRef(false);
    // Wall-clock ms until which the current question's answer window runs; a
    // deferred question may not appear before this even if the highlight was
    // already cleared by the click flow.
    const stageBusyUntilRef = useRef(0);
    const videoRef = React.useRef(null);
    const pollingRef = React.useRef(null);
    const timeoutRef = React.useRef(null);
    const questionTimerRef = React.useRef(null);
    const [murphyErrorOccurred, setMurphyErrorOccurred] = useState(false);
    const [murphyWaitingWarning, setMurphyWaitingWarning] = useState(false);
    const murphyWarningTimerRef = useRef(null);
    const mountedRef = useRef(true); // Track if component is mounted

    // One getUserMedia request per device at a time. initDevices asks for the
    // camera on mount, and laptop cameras take a second or two to open; a rep
    // who clicked Start before that resolved made startRecording ask again
    // (its cameraStream was still null). Both streams opened, the first to
    // arrive was overwritten in cameraStreamRef, and nothing ever stopped it:
    // the camera indicator stayed lit after leaving (Toby, 2026-10-02). Both
    // callers now share the live stream, or the request already in flight.
    const deviceRequestsRef = useRef({ audio: null, video: null });
    const acquireDevice = (kind) => {
        const streamRef = kind === 'video' ? cameraStreamRef : microphoneStreamRef;
        const current = streamRef.current;
        if (current && current.getTracks().some((track) => track.readyState !== 'ended')) {
            return Promise.resolve(current);
        }
        if (!deviceRequestsRef.current[kind]) {
            const request = navigator.mediaDevices.getUserMedia(kind === 'video' ? { video: true } : { audio: true });
            deviceRequestsRef.current[kind] = request;
            const settle = () => {
                if (deviceRequestsRef.current[kind] === request) deviceRequestsRef.current[kind] = null;
            };
            request.then(settle, settle);
        }
        return deviceRequestsRef.current[kind];
    };

    // Refs for audio context and nodes
    const audioContextRef = useRef(null);
    const destinationStreamRef = useRef(null);
    const micStreamSourceRef = useRef(null);

    // Q&A Practice specific state
    const isQAPractice = meetingStore.meeting?.meetingTypeKey === 'qa_practice';
    const isScreenShareType = SCREEN_SHARE_TYPES.includes(meetingStore.meeting?.meetingTypeKey);
    const qaPlayingQuestionRef = useRef(null); // Guard against playing same question twice
    const [qaAutoCompleting, setQaAutoCompleting] = useState(false);
    const qaAutoCompleteTimerRef = useRef(null);
    const pendingUploadRef = useRef(null); // Track in-flight chunk upload promise
    const confirmInFlightRef = useRef(false); // One dialog confirm at a time
    const [pendingRestart, setPendingRestart] = useState(false);

    const setMeetingType = useCallback(() => {
        if (meetingStore.meeting?.meetingTypeKey) {
            videoPresentationStore.setRecordingType(meetingStore.meeting.meetingTypeKey);
        }
    }, [meetingStore.meeting?.meetingTypeKey]);

    useEffect(() => {
        setMeetingType();
    }, [setMeetingType]);

    // Leaving a one-off Document Defense session drops what the browser holds
    // of it: the upload's key and the question text (the document itself and
    // the expected answers never reach the browser). This used to be a full
    // page reload, which put the app's loading screen between the recording
    // and the feedback. Deferred a tick so child cleanups still see the
    // meeting, and skipped if a new session was launched in the meantime.
    useEffect(() => {
        const isDocQASession = !!meetingStore.documentKey;
        const launchId = meetingStore.activeLaunchId;
        return () => {
            if (isDocQASession) {
                setTimeout(() => meetingStore.clearDocumentSession(launchId), 0);
            }
        };
    }, []);

    const toggleRecording = async () => {
        if (recording) {
            // An unfinished Q&A practice: ask BEFORE stopping, so "Keep
            // practicing" can carry on exactly where the rep was. The recording
            // only stops once they pick Submit, Restart or Discard. Answer
            // detection pauses while the question is open.
            if (isQAPractice && qaSession.sessionState !== 'complete') {
                qaSession.pauseVAD();
                setShowPrompt(true);
                return;
            }
            await stopRecording();
        } else {
            // Start and End are the same button. Left focused after Start, the
            // next Enter (the rep advancing to the next question) clicked End
            // instead: the Q&A shortcut deliberately ignores focused buttons.
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }
            await startRecording();
        }
    };

    // "Keep practicing" from the End dialog: back into the session as it was.
    const keepPracticing = () => {
        setShowPrompt(false);
        qaSession.resumeVAD();
    };
    // Ensure timer setup only runs when meeting data is ready
    useEffect(() => {
        if (
            meetingStore.meeting &&
            meetingStore.meeting.level >= 2 &&
            meetingStore.getInteractionSnapshots().length > 0
        ) {
            // Optionally, you could auto-start recording here, or just ensure timer logic is ready
        } else {
        }
    }, [meetingStore.meeting]);

    const getTimestamp = () => {
        const date = new Date();
        const pad = (num) => num.toString().padStart(2, '0');

        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hour = pad(date.getHours());
        const min = pad(date.getMinutes());
        const sec = pad(date.getSeconds());

        return `${year}${month}${day}${hour}${min}${sec}`;
    };

    const createBlob = useCallback((blob, isFinal = false) => {
        // Don't upload chunks if video data hasn't been initialized
        if (!videoPresentationStore.videoData?.uploadId || !videoPresentationStore.videoData?.key) {
            console.warn('createBlob called before video upload initialization. Skipping chunk upload.');
            return Promise.resolve();
        }

        blobCountRef.current += 1;
        const partNumber = blobCountRef.current;
        const data = {
            userId: usersStore.currentUser.userId,
            ContentType: 'video/mp4',
            partNumber: partNumber,
            companyId: companyStore.company.id,
            // Stamp the part with the session it was QUEUED in. The chain below can
            // hold it until after a Restart, and the store drops anything whose
            // session no longer matches rather than PUTting an old blob into the new
            // multipart under an old part number.
            sessionId: videoPresentationStore.sessionId
        };

        // Chain this upload after the previous one completes.
        // This serializes all uploads so finalizeUpload never races with a pending PUT.
        const prev = pendingUploadRef.current || Promise.resolve();
        const uploadPromise = prev
            .catch(() => {}) // don't let a previous failure block the chain
            .then(() => videoPresentationStore.uploadChunk(data, blob, isFinal));

        pendingUploadRef.current = uploadPromise;
        return uploadPromise;
    }, [companyStore.company.id, usersStore.currentUser.userId, videoPresentationStore]);

    useEffect(() => {
        // Only auto-flush buffer while actively recording — after stopRecording()
        // sets recording=false, onDialogConfirm handles the remaining chunks.
        if (!recording) return;

        const bufferSize = chunksBuffer.reduce((accumulator, currentValue) => {
            return accumulator + currentValue.size;
        }, 0);

        if (bufferSize >= 5 * 1024 * 1024) {
            const blobChunks = new Blob(chunksBuffer, { type: 'video/mp4' });
            setChunksBuffer([]);
            chunksBufferRef.current = []; // Clear ref synchronously so onDialogConfirm sees it
            createBlob(blobChunks);
        }
    }, [chunksBuffer, chunksBuffer.length, createBlob, recording]);

    // Handle data chunks from recording
    const handleDataAvailable = (event) => {
        if (event.data.size > 0) {
            setChunksBuffer((prevChunks) => {
                const newChunks = [...prevChunks, event.data];
                chunksBufferRef.current = newChunks;
                return newChunks;
            });
        }
    };

    // Reference to store cleanup functions and active source nodes
    const timerCleanupRef = React.useRef(null);
    const activeSourcesRef = useRef([]); // Track active audio sources

    // Initialize Web Audio API context and nodes
    const initAudioContext = async (micStream) => {
        try {
            // Clean up existing context if it exists
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                await audioContextRef.current.close();
            }

            audioContextRef.current = new AudioContext();
            const ctx = audioContextRef.current;

            // CRITICAL: Resume AudioContext if it's suspended (required by browsers)
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }

            // Create a MediaStreamDestination to get a stream we can record
            const destinationNode = ctx.createMediaStreamDestination();
            destinationStreamRef.current = destinationNode;

            // Connect microphone to the destination
            const micSource = ctx.createMediaStreamSource(micStream);
            micSource.connect(destinationNode);
            micStreamSourceRef.current = micSource;

            return destinationNode.stream;
        } catch (error) {
            console.error('Error setting up audio context:', error);
            throw error;
        }
    };

    const cleanupAudioResources = useCallback(() => {
        // Clean up any active audio sources
        if (activeSourcesRef.current.length > 0) {
            activeSourcesRef.current.forEach(source => {
                try {
                    source.stop();
                    source.disconnect();
                } catch (e) {
                    console.warn('Error cleaning up audio source:', e);
                }
            });
            activeSourcesRef.current = [];
        }

        // Disconnect and clean up the mic source
        if (micStreamSourceRef.current) {
            try {
                micStreamSourceRef.current.disconnect();
                micStreamSourceRef.current = null;
            } catch (e) {
                console.warn('Error cleaning up mic source:', e);
            }
        }

        // Clean up destination node
        if (destinationStreamRef.current) {
            try {
                destinationStreamRef.current = null;
            } catch (e) {
                console.warn('Error cleaning up destination:', e);
            }
        }

        // Close the audio context
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            try {
                audioContextRef.current.close()
                    .then(() => {
                        audioContextRef.current = null;
                    })
                    .catch(e => {
                        console.warn('Error closing audio context:', e);
                    });
            } catch (e) {
                console.warn('Error closing audio context:', e);
            }
        }
    }, []);

    const startRecording = async () => {
        // Clear any previous recording state before starting new recording
        videoPresentationStore.reset();
        
        // Reset local component state
        blobCountRef.current = 0;
        pendingUploadRef.current = null;
        setChunksBuffer([]);
        chunksBufferRef.current = [];

        // Prevent recording if microphone is muted or camera is disabled
        setMurphyErrorOccurred(false);
        // A confirm that failed or threw leaves "Uploading Video..." in the user
        // tile; only startPolling ever cleared it, so it would sit there through
        // the next screen-share picker and stay forever if the picker is cancelled.
        setVideoStatus('');


        let stream;
        // Mic acquisition runs before the screen-share picker inside the same try.
        // Track whether we reached the picker so the catch can tell a picker-cancel
        // (bail silently) apart from a mic/camera failure (must show a message).
        let reachedScreenSharePicker = false;
        try {
            // Use existing microphone stream or get a new one
            const audioStream = microphoneStream || await acquireDevice('audio');
            if (audioStream !== microphoneStream) {
                microphoneStreamRef.current = audioStream;
                setMicrophoneStream(audioStream);
                // A freshly acquired stream is always live. stopRecording nulls the
                // stream but leaves isMicrophoneActive false if the user had muted,
                // so without this the UI claims "Muted" over a live mic on restart —
                // and pressing Mute to "fix" it would genuinely mute the recording.
                setIsMicrophoneActive(true);
            }

            // Check if mic is muted or disabled. enabled=false means the in-app Mute
            // toggle; muted=true means the browser is getting no audio (OS/hardware mute).
            const micTrack = audioStream.getAudioTracks()[0];
            if (!micTrack.enabled || micTrack.muted) {
                setMurphyErrorOccurred('mic_muted');
                return;
            }

            // Use Web Audio API to mix mic and question audio
            if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
                audioContextRef.current = new window.AudioContext();
            }
            const ctx = audioContextRef.current;
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }
            // Create MediaStreamDestination for mixed audio
            const destinationNode = ctx.createMediaStreamDestination();
            // Speech is mono, and the destination defaults to 2 channels: at
            // 64 kbps a stereo mix would give each voice about 32 kbps. One
            // channel puts the whole budget on the one thing being graded.
            destinationNode.channelCount = 1;
            destinationStreamRef.current = destinationNode;
            // Connect mic to destination
            const micSource = ctx.createMediaStreamSource(audioStream);
            micSource.connect(destinationNode);
            micStreamSourceRef.current = micSource;

            // Use mixed audio stream for recording
            const mixedAudioStream = destinationNode.stream;

            if (isScreenShareType) {
                // Screen + mixed audio capture for sales presentation
                reachedScreenSharePicker = true;
                const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
                cameraStreamRef.current = screenStream;
                setCameraStream(screenStream);
                const combinedStream = new MediaStream([
                    ...screenStream.getVideoTracks(),
                    ...mixedAudioStream.getAudioTracks()
                ]);
                stream = combinedStream;
            } else {
                // Webcam + mixed audio for other types
                const videoStream = cameraStream || await acquireDevice('video');
                if (videoStream !== cameraStream) {
                    cameraStreamRef.current = videoStream;
                    setCameraStream(videoStream);
                }
                const combinedStream = new MediaStream([
                    ...videoStream.getVideoTracks(),
                    ...mixedAudioStream.getAudioTracks()
                ]);
                stream = combinedStream;
            }
        } catch (err) {
            // Cancelling the screen-share picker throws NotAllowedError (AbortError in
            // some browsers) — a genuine cancel, bail silently (preserves commit c90c18d5).
            // Only bail when the throw actually came from the picker: a NotAllowedError
            // from the earlier mic acquisition means access is blocked and used to be
            // swallowed here, leaving Start a silent no-op for screen-share types.
            if (reachedScreenSharePicker && err &&
                (err.name === 'AbortError' || err.name === 'NotAllowedError')) {
                return;
            }

            // Firefox reports a mic/camera held by another app as AbortError from
            // getUserMedia; classifyMediaError doesn't map it, so route it to the
            // device guidance instead of bailing silently.
            const mediaError = classifyMediaError(err) ||
                (err?.name === 'AbortError' ? 'device_not_found' : null);
            const errorToShow = mediaError || 'recording_failed';
            console.error(`Recording start failed (${err?.name}):`, err);
            setMurphyErrorOccurred(errorToShow);
            return;
        }

        // The awaits above (permission prompt, screen-share picker) can outlive the
        // component. By then the unmount cleanup has already run and cannot see a
        // stream acquired after it, so release everything here rather than leaking
        // the device. False on every path where the user is still on screen.
        if (!mountedRef.current) {
            stream.getTracks().forEach((track) => track.stop());
            [microphoneStreamRef, cameraStreamRef].forEach((ref) => {
                const orphan = ref.current;
                ref.current = null;
                if (orphan) orphan.getTracks().forEach((track) => track.stop());
            });
            return;
        }

        // Recorder-setup failure: show the error and, for screen-share types, release
        // the display capture acquired above — otherwise the browser's "sharing your
        // screen" bar persists and each retry stacks another capture.
        const failRecorderSetup = (err) => {
            if (err) console.error('Failed to create MediaRecorder:', err);
            if (isScreenShareType) {
                stream.getVideoTracks().forEach((track) => track.stop());
                setCameraStream(null);
            }
            setMurphyErrorOccurred('recording_failed');
        };

        // Get supported MIME type
        const mimeType = getSupportedMimeType(!isScreenShareType);
        if (!mimeType) {
            failRecorderSetup(null);
            return;
        }

        // Create MediaRecorder with supported MIME type
        let mediaRecorder;
        try {
            mediaRecorder = new MediaRecorder(stream, {
                mimeType,
                // Real prod recordings ran at 2.65 Mbps (91 MB for 4.6 min) from
                // a 640x480 webcam, far above what 480p needs and above what a
                // typical hotspot can carry in real time, so parts piled up
                // behind the recording. These are targets in Chrome, not caps.
                videoBitsPerSecond: isScreenShareType ? 1_500_000 : 1_000_000,
                audioBitsPerSecond: 64_000,
            });
        } catch (err) {
            failRecorderSetup(err);
            return;
        }

        // The stream attaches to the video element via useEffect. Set it only after
        // recorder creation succeeds so a setup failure keeps the live webcam preview
        // (video branch) or falls back to the placeholder (screen-share types) — both
        // branches render the error overlay.
        mediaStreamRef.current = stream;
        setMediaStream(stream);

        setRecording(true);
        setChunksBuffer([]);
        mediaRecorderRef.current = mediaRecorder;
        setMediaRecorder(mediaRecorder);

        // Get the current meeting type
        const meeting = meetingStore.meeting;

        // Setup for level 2+ meetings. The TIMERS start further down, after
        // mediaRecorder.start() succeeds — scheduling them here skewed every
        // Murphy question earlier by the upload-init round trip (300ms-3s),
        // while grading compares the unskewed startTime to the recording clock.
        let startInteractionTimers = null;
        if (meeting.level >= 2) {
            const interactions = meetingStore.getInteractionSnapshots(); // Use safe snapshots

            // Store interactions in the VideoPresentationStore using safe snapshots
            videoPresentationStore.setInteractions(interactions);

            startInteractionTimers = () => {
                // Store cleanup functions for each timer
                const timerCleanups = [];

                // How often a deferred question re-checks whether the stage is
                // free. Scheduling spaces questions 60s apart, so deferral is a
                // backstop for slow clicks / long audio, not the normal path.
                const DEFER_RETRY_MS = 5000;

                // Set up timers for each interaction
                interactions.forEach((interaction) => {
                    const show = () => {
                        // Never preempt: if a question is still being noticed,
                        // heard, or answered, wait. A preempted question could
                        // never be clicked again, was never marked responded,
                        // and graded as ignored (-100) through no fault of the
                        // user's. Grading keys off the interaction's original
                        // startTime, so deferral never affects scoring.
                        const stageBusy =
                            currentInteractionRef.current !== null ||
                            isPlayingAudioRef.current ||
                            Date.now() < stageBusyUntilRef.current;
                        if (stageBusy) {
                            const retryTimer = setTimeout(show, DEFER_RETRY_MS);
                            timerCleanups.push(() => clearTimeout(retryTimer));
                            return;
                        }

                        // Set the current participant and their question. The
                        // ref is written inline (not via the sync effect) so a
                        // second timer firing in the same tick sees it.
                        currentInteractionRef.current = interaction;
                        stageBusyUntilRef.current = Date.now() + interaction.duration * 1000;
                        setCurrentInteraction(interaction);

                        // Clear ONLY this interaction when its window lapses: an
                        // unconditional null here would wipe a successor shown
                        // after this one ended.
                        const cleanupTimer = setTimeout(() => {
                            if (currentInteractionRef.current &&
                                currentInteractionRef.current.startTime === interaction.startTime) {
                                currentInteractionRef.current = null;
                            }
                            setCurrentInteraction(prev =>
                                (prev && prev.startTime === interaction.startTime) ? null : prev
                            );
                        }, interaction.duration * 1000);

                        timerCleanups.push(() => {
                            clearTimeout(cleanupTimer);
                        });
                    };

                    const timer = setTimeout(show, interaction.startTime * 1000);

                    timerCleanups.push(() => {
                        clearTimeout(timer);
                    });
                });

                // Store cleanup function
                timerCleanupRef.current = () => {
                    timerCleanups.forEach(cleanup => cleanup());
                };
            };
        }

        const data = {
            userId: usersStore.currentUser.userId,
            ContentType: 'video/mp4',
            key: `recording_${getTimestamp()}_${meeting.meetingTypeKey}`,
            numberOfTries,
            companyId: companyStore.company.id,
            interactionId: isQAPractice ? meeting.sessionId : meeting.interactionId
        };

        await videoPresentationStore.uploadInitialChunk(data);

        mediaRecorder.ondataavailable = (e) => handleDataAvailable(e);

        mediaRecorder.onerror = (event) => {
            console.error('❌ MediaRecorder error:', event);
        };

        // The capture can die during the awaited upload init above (e.g. the user
        // clicks the browser's native "Stop sharing" bar); start() then throws and
        // the UI would be stuck showing "End Meeting" with nothing recording.
        try {
            mediaRecorder.start(5000);
            // Question timers align with the recording clock: they start only
            // once the recorder is actually capturing.
            if (startInteractionTimers) {
                startInteractionTimers();
            }
        } catch (err) {
            console.error('Failed to start MediaRecorder:', err);
            if (timerCleanupRef.current) {
                timerCleanupRef.current();
                timerCleanupRef.current = null;
            }
            setRecording(false);
            setMediaRecorder(null);
            setMediaStream(null);
            failRecorderSetup(null);
        }
    };

    const stopRecording = async () => {
        // Clean up timers if they exist
        if (timerCleanupRef.current) {
            timerCleanupRef.current();
            timerCleanupRef.current = null;
        }
        if (questionTimerRef.current) {
            clearTimeout(questionTimerRef.current);
            questionTimerRef.current = null;
        }
        // Cancel Q&A auto-complete timer if user manually stops
        if (qaAutoCompleteTimerRef.current) {
            clearTimeout(qaAutoCompleteTimerRef.current);
            qaAutoCompleteTimerRef.current = null;
        }

        if (mediaRecorder) {
            // stop() throws InvalidStateError if the recorder never started or its
            // stream died; keep going so tracks and state still get cleaned up.
            try {
                if (mediaRecorder.state !== 'inactive') {
                    // Await the recorder's onstop before showing the upload
                    // prompt: the FINAL ondataavailable (up to one 5s timeslice
                    // of the last answer) arrives asynchronously after stop().
                    // The Q&A auto-complete path confirms the dialog with no
                    // human delay, so without this the tail of the final answer
                    // was systematically dropped from the upload.
                    const stopped = new Promise((resolve) => {
                        const prevOnStop = mediaRecorder.onstop;
                        mediaRecorder.onstop = (e) => {
                            if (typeof prevOnStop === 'function') prevOnStop(e);
                            resolve();
                        };
                        // Never hang teardown on a recorder whose stream died.
                        setTimeout(resolve, 2000);
                    });
                    mediaRecorder.stop();
                    await stopped;
                }
            } catch (err) {
                console.error('MediaRecorder stop failed:', err);
            }
        }

        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
        }

        if (microphoneStream) {
            microphoneStream.getTracks().forEach((track) => track.stop());
            setMicrophoneStream(null);
        }

        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
            setCameraStream(null);
        }

        // Clean up audio resources
        cleanupAudioResources();

        setCurrentInteraction(null);
        setRecording(false);
        setShowPrompt(true);
        setMediaRecorder(null);
        setMediaStream(null);
    };

    const onDialogConfirm = async () => {
        // One confirm at a time. The same handler is reachable from the dialog's
        // "Yes", the Q&A "Submit for Feedback" button and the Q&A auto-confirm
        // effect, and on a slow link a rep will click again while the first one is
        // still uploading — which used to queue the whole tail chunk a second time.
        if (confirmInFlightRef.current) return;
        confirmInFlightRef.current = true;

        // The recording this confirm belongs to. If a reset lands during the awaits
        // below (unmount, or a Restart), finalizing against the cleared store would
        // report a failure for a recording the user deliberately abandoned.
        const sid = videoPresentationStore.sessionId;

        setUploading(true);
        setVideoStatus('uploading');

        // Read from ref (not state) to get the latest buffer contents.
        // State may be stale due to React batching when effects fire in the same render cycle.
        // Build the blob and clear the buffer BEFORE the first await: clearing it
        // afterwards is what let a second click send the same tail chunk again.
        const remainingChunks = chunksBufferRef.current;
        const mimeType = mediaRecorder ? mediaRecorder.mimeType : 'video/mp4';
        const blobChunks = new Blob(remainingChunks, { type: mimeType });
        setChunksBuffer([]);
        chunksBufferRef.current = [];

        try {
            // Wait for all in-flight chunk uploads to complete (they are chained sequentially).
            if (pendingUploadRef.current) {
                await pendingUploadRef.current;
            }
            if (videoPresentationStore.sessionId !== sid) return;

            if (blobChunks.size) {
                await createBlob(blobChunks, true);
            } else {
                const data = {
                    companyId: companyStore.company.id,
                    userId: usersStore.currentUser.userId,
                    ContentType: 'video/mp4',
                    partNumber: blobCountRef.current + 1,
                };

                await videoPresentationStore.finalizeUpload(data);
            }
            if (videoPresentationStore.sessionId !== sid) return;

            setCurrentInteraction(null);

            // Only treat it as a hard upload failure when *nothing* reached S3 — i.e. zero
            // uploaded parts (e.g. camera/mic blocked, so the recording is empty). Then Murphy
            // has nothing to grade and we show the upload_failed message.
            //
            // If parts DID upload but the store still carries an `error`, that's almost always a
            // transient hiccup on the finalize/last-part response. The object is in S3 and the
            // backend triggers transcription + grading off the S3 event regardless of whether the
            // browser received the finalize response, so the assessment will appear. Proceed to
            // poll instead of showing a false "your recording didn't upload" for a video that did
            // upload. The store already reported the transient error to Sentry.
            const nothingUploaded = videoPresentationStore.videoEtagData.length === 0;

            // uploadFailed is the store's TERMINAL verdict: a part had no upload
            // URL, a PUT exhausted its retries, or finalize failed with a real
            // HTTP error. Some parts may have reached S3, but the multipart can
            // never complete, so no S3 object → no transcription → no assessment.
            // Polling would spin for 12 minutes and end in a generic error.
            if (nothingUploaded || videoPresentationStore.uploadFailed) {
                console.error('Recording upload failed; not polling for assessment.', {
                    error: videoPresentationStore.error,
                    parts: videoPresentationStore.videoEtagData.length,
                    uploadFailed: videoPresentationStore.uploadFailed,
                });
                setVideoStatus('');
                setMurphyErrorOccurred('upload_failed');
                return;
            }

            const uploadUncertain = !!videoPresentationStore.error;
            if (uploadUncertain) {
                console.warn('Upload finished with a transient error but parts reached S3; polling for the grade.', {
                    error: videoPresentationStore.error,
                    parts: videoPresentationStore.videoEtagData.length,
                });
            }

            startPolling(companyStore.company.id, videoPresentationStore.videoData.key, uploadUncertain);
            setVideoStatus('uploading');
        } finally {
            // Whatever happened — including a throw from a null mediaRecorder or
            // videoData — the dialog must never be left stuck in its busy state.
            confirmInFlightRef.current = false;
            if (mountedRef.current) {
                setUploading(false);
                setShowPrompt(false);
            }
        }
    };

    const onDialogCancel = async () => {
        // Reached straight from End meeting in a Q&A practice, the recording is
        // still running: stop it first, as End used to.
        if (recording) await stopRecording();
        setNumberOfTries(numberOfTries + 1);
        setShowPrompt(false);
        setChunksBuffer([]);
        chunksBufferRef.current = [];
        blobCountRef.current = 0;

        // Reset Q&A session so it re-initializes with the same questions
        if (isQAPractice) {
            qaSession.resetSession();
            qaPlayingQuestionRef.current = null; // Allow questions to play again
            setQaAutoCompleting(false);
            // Flag restart — useEffect will call startRecording after React settles state
            setPendingRestart(true);
        }
    };

    const fetchAssessment = async (companyId, key) => {
        if (!mountedRef.current) return; // Don't proceed if component is unmounted

        const assessments = await assessmentsStore.searchForAssessmentByVideoName(companyId, key);

        if (!mountedRef.current) return; // Check again after async operation

        if (assessments.length === 0) {
            setVideoStatus('transcribing');
        }

        const assessment = assessments[0];

        if (assessment) {
            // If grading failed, Murphy will retry in the background — show appropriate message
            if (assessment.gradingStatus === 'permanently_failed') {
                stopPolling();
                setMurphyErrorOccurred('permanently_failed');
                return;
            }
            if (assessment.gradingStatus === 'failed') {
                stopPolling();
                setMurphyErrorOccurred(true);
                return;
            }

            if (!assessment.grade || assessment.grade === null || assessment.grade === '') {
                setVideoStatus('grading');
            } else {
                setVideoStatus('graded');
                // A poll already in flight when the 12-minute timeout fired
                // resolves after it: the grade is real, so the timeout's error
                // (and its "we'll email you" promise, which the status display
                // checks first) must not outrank it (review, 2026-09-04).
                setMurphyErrorOccurred(false);
                stopPolling();
            }
        }
    };

    const startPolling = (companyId, key, uploadUncertain = false) => {
        if (!mountedRef.current) return; // Don't start polling if component is unmounted

        setMurphyErrorOccurred(false);
        setVideoStatus('');
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }

        if (timeoutRef.current) {
            clearInterval(timeoutRef.current);
        }

        // 12 min timeout to allow for transcription + in-Lambda retries on long recordings.
        // When the upload finished with a lingering error (uploadUncertain), a timeout means
        // the finalize most likely never completed and no video exists in S3 — show the
        // accurate "didn't upload" message instead of the generic "your video has been
        // saved" copy, which would be false for a video that was never saved.
        timeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
                stopPolling();
                setMurphyErrorOccurred(uploadUncertain ? 'upload_failed' : true);
            }
        }, 12 * 60 * 1000);
        pollingRef.current = setInterval(() => fetchAssessment(companyId, key), 5000); // Start polling
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    // Handle video element and stream connection
    useEffect(() => {
        if (mediaStream && videoRef.current) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.play().catch(error => {
                console.error('Error playing video:', error);
            });
        }

        // Cleanup function
        return () => {
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
        };
    }, [mediaStream]);

    const [isPlayingAudio, setIsPlayingAudio] = useState(false);

    // Keep the timer-visible refs in step with state set from ANY site (the QA
    // flow writes currentInteraction too). The inline ref writes in the timer
    // path cover same-tick reads; this covers everything else.
    useEffect(() => { currentInteractionRef.current = currentInteraction; }, [currentInteraction]);
    useEffect(() => { isPlayingAudioRef.current = isPlayingAudio; }, [isPlayingAudio]);
    const [preloadedAudioUrl, setPreloadedAudioUrl] = useState(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerCleanupRef.current) {
                timerCleanupRef.current();
            }
            if (questionTimerRef.current) {
                clearTimeout(questionTimerRef.current);
            }
            if (murphyWarningTimerRef.current) {
                clearTimeout(murphyWarningTimerRef.current);
            }
            if (qaAutoCompleteTimerRef.current) {
                clearTimeout(qaAutoCompleteTimerRef.current);
            }
            // Capture-device teardown lives in the unmount effect further down;
            // it reads refs, which is the only thing that works from a [] deps
            // cleanup. A stop() call here would close over a null stream.
            stopPolling();
            confirmInFlightRef.current = false;
            // Clear video presentation store state when leaving the component
            videoPresentationStore.reset();
        };
    }, []);

    const ensureAudioContext = async () => {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            audioContextRef.current = new AudioContext();
        }

        if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
        }

        return audioContextRef.current;
    };

    const playQuestionWithMixing = async (questionText, participantName, onEnded) => {
        try {
            const audioContext = await ensureAudioContext();
            if (!audioContext) return false;

            // Get base64 audio from lambda
            const base64Audio = await meetingStore.generateAudio(questionText, participantName);
            if (!base64Audio) {
                throw new Error('No audio returned from lambda');
            }

            // Convert base64 to ArrayBuffer
            const binaryString = window.atob(base64Audio);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const arrayBuffer = bytes.buffer;

            // Decode and play
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            // Connect to speakers
            source.connect(audioContext.destination);
            // Also connect to recording stream (MediaStreamDestination)
            if (destinationStreamRef.current) {
                source.connect(destinationStreamRef.current);
            }
            source.start(0);

            setIsPlayingAudio(true);
            source.onended = () => {
                setIsPlayingAudio(false);
                if (typeof onEnded === 'function') {
                    onEnded();
                }
            };

            return true;
        } catch (error) {
            console.warn('Failed to play base64 audio:', error);
            return false;
        }
    };

    // Update handleParticipantClick to use the new audio mixing
    const handleParticipantClick = useCallback((participant) => {
        // Clear Murphy warning when user responds
        if (currentInteraction && String(currentInteraction.participantName) === String(participant.name)) {
            setMurphyWaitingWarning(false);
            if (murphyWarningTimerRef.current) {
                clearTimeout(murphyWarningTimerRef.current);
                murphyWarningTimerRef.current = null;
            }
        }

        // Only play audio if the clicked participant is the highlighted one and audio is not already playing
        if (currentInteraction && String(currentInteraction.participantName) === String(participant.name) && !isPlayingAudio) {
            // Track that the user responded to this interaction
            videoPresentationStore.markInteractionResponded(currentInteraction.startTime);
            setIsPlayingAudio(true);
            const respondedInteraction = currentInteraction;
            playQuestionWithMixing(
                respondedInteraction.text,
                respondedInteraction.participantName,
                () => {
                    // Remove the flashing indicator after audio ends — but only
                    // for THIS interaction, never a successor shown since.
                    if (currentInteractionRef.current &&
                        currentInteractionRef.current.startTime === respondedInteraction.startTime) {
                        currentInteractionRef.current = null;
                    }
                    setCurrentInteraction(prev =>
                        (prev && prev.startTime === respondedInteraction.startTime) ? null : prev
                    );
                }
            )
                .then(success => {
                    if (!success) {
                        console.error('Failed to play audio');
                        setIsPlayingAudio(false);
                    }
                })
                .catch(error => {
                    console.error('Error playing audio:', error);
                    setIsPlayingAudio(false);
                });
        }
    }, [currentInteraction, isPlayingAudio, playQuestionWithMixing]);

    // Murphy warning timer - show warning if user doesn't respond within 5 seconds
    useEffect(() => {
        // Clear any existing warning timer
        if (murphyWarningTimerRef.current) {
            clearTimeout(murphyWarningTimerRef.current);
            murphyWarningTimerRef.current = null;
        }

        // Reset warning state when interaction changes
        setMurphyWaitingWarning(false);

        // If Murphy (or any participant) is asking a question, start 5-second warning timer
        if (currentInteraction && currentInteraction.participantName) {
            murphyWarningTimerRef.current = setTimeout(() => {
                if (mountedRef.current) {
                    setMurphyWaitingWarning(true);
                }
            }, 5000); // 5 seconds
        }

        // Cleanup function
        return () => {
            if (murphyWarningTimerRef.current) {
                clearTimeout(murphyWarningTimerRef.current);
                murphyWarningTimerRef.current = null;
            }
        };
    }, [currentInteraction]);

    // Add cleanup for audio context
    useEffect(() => {
        return () => {
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close()
                    .then(() => {
                        audioContextRef.current = null;
                    })
                    .catch(err => {
                        console.warn('Error closing AudioContext:', err);
                    });
            }
        };
    }, []);

    const [isMicrophoneActive, setIsMicrophoneActive] = useState(true);
    const [microphoneStream, setMicrophoneStream] = useState(null);
    const microphoneStreamRef = useRef(null);

    // Keep microphoneStream ref in sync with state (for useQASession)
    useEffect(() => {
        microphoneStreamRef.current = microphoneStream;
    }, [microphoneStream]);

    // Function to toggle microphone state
    const toggleMicrophone = useCallback(async () => {
        if (microphoneStream) {
            microphoneStream.getTracks().forEach(track => {
                track.enabled = !track.enabled;
                setIsMicrophoneActive(track.enabled);
            });
        } else if (mediaStream) {
            // If we have a mediaStream but no separate microphoneStream, toggle audio tracks
            const audioTracks = mediaStream.getAudioTracks();
            audioTracks.forEach(track => {
                track.enabled = !track.enabled;
                setIsMicrophoneActive(track.enabled);
            });
        }
    }, [mediaStream, microphoneStream]);

    // Add effect to initialize microphone state
    useEffect(() => {
        if (mediaStream) {
            const audioTracks = mediaStream.getAudioTracks();
            if (audioTracks.length > 0) {
                setIsMicrophoneActive(audioTracks[0].enabled);
            }
        }
    }, [mediaStream]);

    const [isCameraActive, setIsCameraActive] = useState(true);
    const [cameraStream, setCameraStream] = useState(null);
    const cameraStreamRef = useRef(null);

    // Function to toggle camera state
    const toggleCamera = useCallback(async () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => {
                track.enabled = !track.enabled;
                setIsCameraActive(track.enabled);
            });
        } else if (mediaStream) {
            // If we have a mediaStream but no separate cameraStream, toggle video tracks
            const videoTracks = mediaStream.getVideoTracks();
            videoTracks.forEach(track => {
                track.enabled = !track.enabled;
                setIsCameraActive(track.enabled);
            });
        }
    }, [mediaStream, cameraStream]);

    // Add effect to initialize camera state
    useEffect(() => {
        if (mediaStream) {
            const videoTracks = mediaStream.getVideoTracks();
            if (videoTracks.length > 0) {
                setIsCameraActive(videoTracks[0].enabled);
            }
        }
    }, [mediaStream]);

    // Initialize devices on mount only - separate from the callback
    useEffect(() => {
        const initDevices = async () => {
            try {
                if (!microphoneStream && mountedRef.current) {
                    const audioStream = await acquireDevice('audio');

                    if (mountedRef.current) {
                        // Assign the ref alongside state: the sync effect only
                        // commits after paint, and unmount can land in between.
                        microphoneStreamRef.current = audioStream;
                        setMicrophoneStream(audioStream);
                        setIsMicrophoneActive(true);
                    } else {
                        // Component was unmounted, cleanup the stream
                        audioStream.getTracks().forEach(track => track.stop());
                    }
                }

                if (!cameraStream && mountedRef.current && !isScreenShareType) {
                    const videoStream = await acquireDevice('video');
                    if (mountedRef.current) {
                        cameraStreamRef.current = videoStream;
                        setCameraStream(videoStream);
                        setIsCameraActive(true);

                        // Set the preview video
                        if (videoRef.current) {
                            videoRef.current.srcObject = videoStream;
                            // Only try to play if not already playing or if video element is ready
                            if (videoRef.current.paused) {
                                videoRef.current.play().catch(error => {
                                    // Ignore abort errors as they're expected when switching video sources
                                    if (error.name !== 'AbortError') {
                                        console.error('Error playing preview video:', error);
                                    }
                                });
                            }
                        }
                    } else {
                        // Component was unmounted, cleanup the stream
                        videoStream.getTracks().forEach(track => track.stop());
                    }
                }
            } catch (err) {
                console.error('Error initializing media devices:', err);
                // Surface a no-device / blocked-access problem on the recording screen right
                // away, before the user clicks Start, rather than swallowing it.
                const mediaError = classifyMediaError(err);
                if (mediaError && mountedRef.current) {
                    setMurphyErrorOccurred(mediaError);
                }
            }
        };

        initDevices();
    }, [meetingStore.meeting?.meetingTypeKey]); // Only depend on recordingType

    // Mirror the live capture handles into refs for the unmount cleanup below.
    // That cleanup has [] deps, so its closure is the FIRST render, where every
    // stream is still null. Reading state there is why its stop() calls were
    // dead code and the tab's camera indicator stayed lit after leaving the
    // screen. microphoneStreamRef is synced at its declaration (useQASession
    // also reads it), so it is not repeated here.
    useEffect(() => {
        cameraStreamRef.current = cameraStream;
        mediaStreamRef.current = mediaStream;
        mediaRecorderRef.current = mediaRecorder;
    }, [cameraStream, mediaStream, mediaRecorder]);

    // Release every capture device on unmount, on EVERY exit path: never
    // recorded, recorded then stopped, or navigated away mid-recording (the
    // NavigationBlocker prompt unmounts us without calling stopRecording).
    // Reads refs only, never state — see the sync effect above.
    useEffect(() => {
        // Never otherwise re-armed, so a remount would leave this false and make
        // initDevices stop every stream it acquires.
        mountedRef.current = true;

        return () => {
            // First: initDevices' pending-getUserMedia guards read this.
            mountedRef.current = false;

            // Stop the recorder ahead of its tracks so the final chunk is well
            // formed and it releases its own reference. Throws InvalidStateError
            // if it never started or its stream already ended; harmless.
            if (mediaRecorderRef.current) {
                try {
                    mediaRecorderRef.current.stop();
                } catch (err) {
                    console.warn('MediaRecorder stop on unmount failed:', err);
                }
                mediaRecorderRef.current = null;
            }

            // For webcam types mediaStream shares its video track object with
            // cameraStream; for screen-share types cameraStream IS the display
            // capture. Stopping an already-stopped track is a no-op, so covering
            // all three handles every combination.
            [microphoneStreamRef, cameraStreamRef, mediaStreamRef].forEach((ref) => {
                const stream = ref.current;
                ref.current = null;
                if (stream) {
                    stream.getTracks().forEach((track) => track.stop());
                }
            });
        };
    }, []);

    // Get supported MIME type for MediaRecorder.
    //
    // VP9 for the webcam only. getDisplayMedia captures at the native display
    // resolution (often 1440p or more), Chrome's VP9 encoder is software, and
    // mediaRecorder.onerror only logs with no dropped-frame detection anywhere,
    // so a low-end laptop sharing its screen would degrade silently. VP8 at
    // 1.5 Mbps is still far smaller than today for slides. Browsers without VP9
    // (Firefox's MediaRecorder cannot encode it) fall through unchanged.
    const getSupportedMimeType = (preferVp9 = false) => {
        const types = preferVp9
            ? [
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp8,opus',
                'video/webm;codecs=h264,opus',
                'video/webm',
                'video/mp4'
            ]
            : [
                'video/webm;codecs=vp8,opus',
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=h264,opus',
                'video/webm',
                'video/mp4'
            ];

        return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
    };

    // Q&A Session hook (for Q&A Practice mode)
    // Pass refs so the hook always gets current values (audioContext is created after recording starts)
    const qaSession = useQASession({
        audioContextRef: audioContextRef,
        microphoneStreamRef: microphoneStreamRef,
        destinationNodeRef: destinationStreamRef,
        playQuestionAudio: playQuestionWithMixing,
        onSessionComplete: (summary) => {
            console.log('Q&A Session complete:', summary);
            // Show "Session Complete!" for 3 seconds, then auto-stop and upload
            qaAutoCompleteTimerRef.current = setTimeout(() => {
                setQaAutoCompleting(true);
            }, 3000);
        }
    });

    // Initialize Q&A session when meeting data is ready (Q&A Practice only)
    useEffect(() => {
        if (isQAPractice && meetingStore.meeting?.questions?.length > 0 && !qaSession.isInitialized) {
            // Convert MST models to plain objects for the session manager
            const questions = meetingStore.meeting.questions.map(q => ({
                index: q.index,
                rephrasedQuestion: q.rephrasedQuestion,
                participantName: q.participantName,
                relatedTo: q.relatedTo
            }));
            console.log('Q&A Practice: Initializing with questions:', questions);
            qaSession.initializeSession({
                sessionId: meetingStore.meeting.sessionId,
                questions: questions
            });
        }
    }, [isQAPractice, meetingStore.meeting, qaSession.isInitialized]);

    // Q&A Practice: Auto-play question when state changes to MURPHY_ASKING
    useEffect(() => {
        if (!isQAPractice || !recording) return;

        const playCurrentQuestion = async () => {
            if (qaSession.sessionState === QA_STATES.MURPHY_ASKING && qaSession.currentQuestion) {
                const question = qaSession.currentQuestion;

                // Guard: don't play the same question twice (protects against effect re-fires)
                if (qaPlayingQuestionRef.current === question.index) return;
                qaPlayingQuestionRef.current = question.index;

                qaSession.notifyTTSStarted();

                try {
                    const success = await playQuestionWithMixing(
                        question.rephrasedQuestion,
                        question.participantName,
                        () => {
                            qaSession.notifyTTSEnded();
                        }
                    );
                    // If playQuestionWithMixing fails silently (returns false),
                    // the onEnded callback won't fire, so we must manually advance
                    if (!success) {
                        console.warn('Q&A Practice: TTS playback failed, advancing anyway');
                        qaSession.notifyTTSEnded();
                    }
                } catch (error) {
                    console.error('Failed to play Q&A question:', error);
                    qaSession.notifyTTSEnded();
                }
            }
        };

        playCurrentQuestion();
    }, [isQAPractice, recording, qaSession.sessionState, qaSession.currentQuestion]);

    // Q&A Practice: surface a session-level failure (e.g. VAD never started —
    // without it every question dead-airs for 90s and records as skipped).
    // Rendered via the same MurphyStatusDisplay path as other recording errors.
    useEffect(() => {
        if (isQAPractice && qaSession.error && recording) {
            console.error('Q&A session error — stopping recording:', qaSession.error);
            // AWAIT stopRecording: it is async (waits for the recorder's
            // onstop) and sets showPrompt(true) at the END — firing
            // setShowPrompt(false) before it finished let the upload dialog
            // pop on top of the failure screen seconds later.
            (async () => {
                await stopRecording();
                setShowPrompt(false);
                setMurphyErrorOccurred('recording_failed');
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isQAPractice, qaSession.error, recording]);

    // Q&A Practice: Start session when recording starts
    useEffect(() => {
        if (isQAPractice && recording && qaSession.isInitialized && qaSession.sessionState === QA_STATES.IDLE) {
            // Brief delay to ensure audio context is ready. Tracked so an
            // unmount inside the window cannot start a session on a hook whose
            // cleanup has already run (orphaned countdown + timers; review,
            // 2026-09-04).
            const startId = setTimeout(() => {
                qaSession.startSession(3); // 3 second countdown
            }, 500);
            return () => clearTimeout(startId);
        }
        return undefined;
    }, [isQAPractice, recording, qaSession.isInitialized, qaSession.sessionState]);

    // Q&A Practice: Auto-stop recording when session completes
    useEffect(() => {
        if (qaAutoCompleting && recording) {
            stopRecording();
        }
    }, [qaAutoCompleting]);

    // Q&A Practice: Auto-confirm upload (skip the prompt) when auto-completing
    useEffect(() => {
        if (qaAutoCompleting && showPrompt) {
            // Set both false together so the next render never has
            // showPrompt=true + qaAutoCompleting=false (which would flash the popup)
            setQaAutoCompleting(false);
            setShowPrompt(false);
            onDialogConfirm();
        }
    }, [showPrompt, qaAutoCompleting]);

    // Q&A Practice: Auto-restart recording after reset
    useEffect(() => {
        if (pendingRestart && !recording) {
            setPendingRestart(false);
            startRecording();
        }
    }, [pendingRestart, recording]);

    // Training Plan "next up" for the post-grade message. A callback (not a
    // value) because this component is not an observer: MurphyStatusDisplay
    // calls it after the assessments refresh, so the just-graded session is
    // included in the derived plan progress.
    const getTrainingPlanNextUp = useCallback(() => {
        try {
            const plan = computePlanState({
                currentUser: usersStore?.currentUser,
                company: companyStore?.company,
                assessmentsStore,
            });
            if (!plan.panelVisible || plan.planComplete) return null;
            return {
                dayNumber: plan.currentDayNumber,
                todayComplete: plan.todayComplete,
                doneCount: plan.todayDoneCount,
                totalCount: plan.todayRequiredCount,
                nextTitle: plan.nextPractice?.slot?.title || null,
                nextMinutes: plan.nextPractice?.slot?.estimatedMinutes || null,
            };
        } catch (err) {
            console.error('Failed to compute training plan status:', err);
            return null;
        }
    }, [usersStore, companyStore, assessmentsStore]);

    // No meeting in the (in-memory, non-persisted) store. Every real entry path constructs
    // the meeting before navigating here, so this only happens on a hard refresh or a direct
    // nav to the recording URL — there's no context to record against and nothing rebuilds it
    // (some types, e.g. Document Defense, can't be rebuilt at all). Send the user back to the
    // v2 Training Hub to start fresh instead of stranding them on a permanent "Loading…" spinner.
    if (!meetingStore.meeting) {
        return <Redirect to="/training" />;
    }


    // One tile per question-asking persona, or a synthetic camera-off Murphy
    // when a mode has no participants at all. Hoisted so the grid can count the
    // tiles it will actually render: participants.length + 1 undercounts in
    // exactly the synthetic-Murphy case, which is most of L1.
    const tileParticipants = meetingStore.meeting.participants?.length > 0
        ? meetingStore.meeting.participants
        : [{ name: 'Murphy', isMurphy: true }];
    const tileCount = tileParticipants.length + 1; // + the user's own tile

    return (
        <>
            {/* Modal dialogs */}
            {showPrompt && !qaAutoCompleting && isQAPractice && qaSession.sessionState !== 'complete' ? (
                <RecordingNavigationBlocker
                    onConfirm={async () => {
                        // Still recording when reached straight from End meeting.
                        if (recording) await stopRecording();
                        // Discarding is deliberate: drop the leave-the-page guard in the
                        // same commit as the navigation (flushSync) so the router prompt
                        // does not stack a second modal on top of the answer just given.
                        flushSync(() => {
                            setRecording(false);
                            setUploading(false);
                            setShowPrompt(false);
                        });
                        history.push('/training');
                    }}
                    onCancel={onDialogCancel}
                    onThirdOption={async () => {
                        if (recording) await stopRecording();
                        await onDialogConfirm();
                    }}
                    onKeepGoing={recording ? keepPracticing : undefined}
                    keepGoingLabel="Keep practicing"
                    message="Session incomplete. What would you like to do?"
                    confirmLabel="Discard & Return to Training Hub"
                    cancelLabel="Restart"
                    thirdOptionLabel="Submit for Feedback"
                    busy={uploading}
                    // Leaving unmounts the screen, which resets the store and aborts the
                    // PUT cleanly. That is the escape hatch when retries run long.
                    confirmEnabledWhileBusy
                />
            ) : showPrompt && !qaAutoCompleting ? (
                <RecordingNavigationBlocker onConfirm={onDialogConfirm} onCancel={onDialogCancel} busy={uploading} />
            ) : null}
            <NavigationBlocker
                when={recording || uploading}
                prompt={uploading
                    ? 'Your recording is still uploading. Leave anyway?'
                    : 'Are you sure you want to leave? The call is still in progress.'}
            />

            {/* Main layout — Teams-style equal-tile stage + bottom control bar.
                The user's camera and every question-asking persona (or a synthetic
                camera-off "Murphy" when a mode has no participants) get equal
                tiles; the asking tile lights up (bright while the question audio
                plays, soft while awaiting the answer — tileGlow.js). */}
            <ConferenceContainer>
                <TileGrid $centerLast={tileCount % 2 === 1}>
                    <UserTile
                        videoRef={videoRef}
                        hasStream={!!(mediaStream || cameraStream)}
                        isCameraActive={isCameraActive}
                        isScreenShare={isScreenShareType}
                        userName={usersStore.currentUser?.firstName}
                        microphoneStream={microphoneStream}
                        isMicrophoneActive={isMicrophoneActive}
                        recording={recording}
                        placeholder={(
                            <div className="video-placeholder">
                                <div className="placeholder-content">
                                    <img src={posterImg} alt="Video camera" />
                                    <p>Ready to start the call</p>
                                    <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>Click the start button when you're ready to begin</p>
                                    {meetingStore.meeting.meetingTypeKey === 'investor_pitch' && (
                                        <InvestorPitchChecklist />
                                    )}
                                </div>
                                {(videoStatus || murphyErrorOccurred) && (
                                    <div className="status-overlay">
                                        <MurphyStatusDisplay
                                            videoStatus={videoStatus}
                                            error={murphyErrorOccurred}
                                            loadNewAssessment={() => assessmentsStore.loadCompanyAssessments(companyStore.company.id)}
                                            getTrainingPlanNextUp={getTrainingPlanNextUp}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    />

                    {tileParticipants.map((participant) => {
                        const glow = participantGlow({
                            name: participant.name,
                            isQAPractice,
                            sessionState: qaSession.sessionState,
                            currentQuestionName: qaSession.currentQuestion?.participantName,
                            currentInteractionName: currentInteraction?.participantName,
                            isPlayingAudio
                        });
                        return (
                            <ParticipantTile
                                key={participant.name}
                                name={participant.name}
                                avatar={participant.avatar}
                                avatarColor={participant.avatarColor}
                                isMurphy={participant.isMurphy === true}
                                listeningHint={participant.isMurphy === true}
                                glow={glow}
                                caption={tileCaption({ glow, isQAPractice })}
                                onClick={(!isQAPractice && !participant.isMurphy)
                                    ? () => handleParticipantClick(participant)
                                    : undefined}
                            />
                        );
                    })}
                </TileGrid>

                {/* L3 pre-roll countdown, over the whole stage */}
                {isQAPractice && qaSession.sessionState === QA_STATES.COUNTDOWN && qaSession.countdown != null && (
                    <CountdownOverlay data-testid="qa-countdown">
                        <span>{qaSession.countdown}</span>
                    </CountdownOverlay>
                )}

                {/* Murphy waiting warning — one overlay for both stream states
                    (previously duplicated across the video and placeholder branches) */}
                {murphyWaitingWarning && currentInteraction && (
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        right: '20px',
                        zIndex: 100,
                        backgroundColor: '#ffeb3b',
                        color: '#333',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '2px solid #ff9800',
                        animation: 'pulse 1s infinite',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            ⚠️ Murphy is waiting for you to answer a question!
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>
                            Click on the glowing attendee to respond and avoid a penalty.
                        </div>
                    </div>
                )}

                {/* Recording-error overlay for the camera-preview state. The full
                    status display renders inside the user tile's placeholder, but
                    webcam types show the preview from mount — a start-recording
                    failure must surface on top of it or it's invisible. */}
                {(mediaStream || cameraStream) && murphyErrorOccurred && !recording && (
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        right: '20px',
                        zIndex: 100
                    }}>
                        <MurphyStatusDisplay
                            videoStatus=""
                            error={murphyErrorOccurred}
                            loadNewAssessment={() => assessmentsStore.loadCompanyAssessments(companyStore.company.id)}
                        />
                    </div>
                )}

                {/* Bottom control bar: timer left, call controls center, Q&A progress right */}
                <BottomBar>
                    <div className="bar-left">
                        <DurationDisplay
                            recording={recording}
                            pacingConfig={meetingStore.meeting.meetingTypeKey === 'investor_pitch' ? {
                                greenUntil: 420,
                                yellowUntil: 600,
                                targetLabel: 'Target: 7-10 min'
                            } : null}
                        />
                    </div>
                    <div className="bar-center">
                        <RecordingButton
                            icon="microphone"
                            active={isMicrophoneActive}
                            onClick={toggleMicrophone}
                            label="Mute"
                            title={isMicrophoneActive ? "Mute Microphone" : "Unmute Microphone"}
                        />
                        <RecordingButton
                            icon="video"
                            active={isCameraActive}
                            onClick={toggleCamera}
                            label="Camera"
                            title={isCameraActive ? "Turn Off Camera" : "Turn On Camera"}
                        />
                        <RecordingButton
                            recording={recording}
                            toggleRecording={toggleRecording}
                            icon="phone"
                            label={recording ? "End Meeting" : "Start Meeting"}
                            title="Start/Stop Recording"
                        />
                    </div>
                    <div className="bar-right">
                        {isQAPractice && recording && (
                            <QAProgressIndicator
                                state={qaSession.sessionState}
                                currentQuestion={qaSession.progress.currentQuestion}
                                totalQuestions={qaSession.progress.totalQuestions}
                                skippedCount={qaSession.progress.skippedCount}
                                countdown={qaSession.countdown}
                                participantName={qaSession.currentQuestion?.participantName || 'Murphy'}
                                onNextQuestion={qaSession.nextQuestion}
                            />
                        )}
                    </div>
                </BottomBar>
            </ConferenceContainer>
        </>
    );
};

export default NewRecordPresentation;